import React from "react";
import { arrayOf, shape, string } from "prop-types";
import { Box } from "@mui/material";

import DOMPurify from "dompurify";
import Attachments from "./Attachments";

const HTMLContentBlock = ({ dirtyHTML = "", attachments = [] }) => {
  const cleanContent = DOMPurify.sanitize(dirtyHTML, {
    ADD_ATTR: ["target"],
  });
  return (
    <Box
      sx={(theme) => ({
        paddingTop: theme.spacing(0.75),
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
        // Force all child elements to respect width
        "& *": {
          maxWidth: "100%",
          boxSizing: "border-box",
        },
        // Force word wrapping on all text elements
        "& p, & div, & span, & li, & td, & th": {
          wordWrap: "break-word",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        },
        // Handle tables
        "& table": {
          width: "100%",
          tableLayout: "auto",
          maxWidth: "100%",
          display: "block",
          overflowX: "auto",
        },
        // Handle images
        "& img": {
          maxWidth: "100%",
          height: "auto",
          display: "block",
        },
        // Handle code blocks
        "& pre, & code": {
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          maxWidth: "100%",
          overflow: "auto",
          display: "block",
        },
        // Handle iframes and embedded content
        "& iframe, & embed, & object, & video": {
          maxWidth: "100%",
          width: "100%",
        },
        "& p": {
          fontSize: "16px !important",
          color: "#4A4A4A",
          lineHeight: "22px",
          marginTop: 0,
          "&:last-of-type": {
            marginBottom: 0,
          },
          "& br": {
            display: "none",
          },
        },
        "& a": {
          textDecoration: "underline",
          "&:hover": {
            textDecoration: "none",
          },
        },
        "& ul li, & ol li": {
          "&, & span": {
            fontSize: "16px !important",
          },
          color: "#4A4A4A",
          lineHeight: "20px",
          marginBottom: theme.spacing(0.75),
          paddingRight: theme.spacing(2),
        },
        "& ul li": {
          "&::marker": {
            color: theme.palette.secondary.main,
            fontSize: 20,
          },
        },
        "& ol li": {
          "&::marker": {
            color: theme.palette.secondary.main,
            fontWeight: "bold",
            fontSize: 16,
          },
        },
        "& h1": {
          fontSize: 20,
          lineHeight: "26px",
          fontWeight: 600,
          fontFamily: "Lato",
          color: "#4A4A4A",
          marginTop: 0,
        },
        "& h2": {
          fontSize: 16,
          lineHeight: "20px",
          fontFamily: "Lato",
          color: "#4A4A4A",
          marginTop: 0,
        },
        "& blockquote": {
          marginLeft: 0,
          paddingLeft: 40,
          paddingRight: theme.spacing(3),
          position: "relative",
          fontSize: "16px !important",
          color: "#4A4A4A",
          lineHeight: "22px",
          "&::before": {
            content: "'\"'",
            fontSize: 60,
            position: "absolute",
            display: "block",
            fontFamily: "cursive",
            top: 0,
            left: 5,
            color: theme.palette.secondary.light,
            lineHeight: 1,
            height: 30,
            width: 30,
            overflow: "hidden",
          },
        },
      })}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
        dangerouslySetInnerHTML={{ __html: cleanContent }}
      />
      {attachments.length > 0 && (
        <Attachments attachments={attachments} title="Related Resources" />
      )}
    </Box>
  );
};

HTMLContentBlock.propTypes = {
  dirtyHTML: string,
  attachments: arrayOf(
    shape({
      url: string,
      filename: string,
    }),
  ),
};

export default HTMLContentBlock;
